import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { Camera } from '../renderer/Camera';
import type { WorldConfig, WorldInfo } from '../config/WorldConfig';
import { LRUCache } from '../core/LRUCache';
import { Cluster } from './Cluster';
import { worldToAxial } from '../math/HexMath';

/** Max new clusters created per frame to prevent spikes. */
const CLUSTERS_PER_FRAME = 9;

/**
 * Manages cluster loading/unloading based on camera position.
 * Uses staggered loading: new clusters are spread across frames
 * to avoid frame drops when scrolling large worlds.
 */
export class ClusterManager implements GameModule {
  readonly name = 'ClusterManager';
  readonly worldInfo: WorldInfo;

  activeCount = 0;

  private _config: WorldConfig;
  private _cache: LRUCache<string, Cluster>;
  private _engine!: Engine;
  private _camera: Camera;
  private _lastCamCol = -9999;
  private _lastCamRow = -9999;

  get cameraClusterCol(): number { return this._lastCamCol; }
  get cameraClusterRow(): number { return this._lastCamRow; }

  private readonly _loadRadius = 2;

  private _pendingCols: number[] = [];
  private _pendingRows: number[] = [];
  private _pendingIdx = 0;

  constructor(camera: Camera, worldInfo: WorldInfo) {
    this._camera = camera;
    this._config = worldInfo.config;
    this.worldInfo = worldInfo;
    this._cache = new LRUCache<string, Cluster>(
      this._config.maxActiveClusters,
    );
  }

  init(engine: Engine): void {
    this._engine = engine;
  }

  update(_dt: number): void {
    const wx = this._camera.target.x;
    const wz = this._camera.target.z;
    const axial = worldToAxial(wx, wz, this._config.hexRadius);

    const globalRow = axial.r;
    const off = (globalRow + (globalRow & 1)) >> 1;
    const globalCol = axial.q + off;

    const camCol = Math.floor(globalCol / this._config.clusterWidth);
    const camRow = Math.floor(globalRow / this._config.clusterHeight);

    if (camCol !== this._lastCamCol || camRow !== this._lastCamRow) {
      this._lastCamCol = camCol;
      this._lastCamRow = camRow;
      this._buildQueue(camCol, camRow);
    }

    if (this._pendingIdx < this._pendingCols.length) {
      this._processQueue();
    }
  }

  forEachCluster(fn: (cluster: Cluster) => void): void {
    for (const c of this._cache.values()) {
      fn(c);
    }
  }

  dispose(): void {
    this._cache.clear();
    this._pendingCols.length = 0;
    this._pendingRows.length = 0;
  }

  /**
   * Touch existing clusters (prevent LRU eviction) and queue
   * missing ones for staggered creation.
   */
  private _buildQueue(centerCol: number, centerRow: number): void {
    this._pendingCols.length = 0;
    this._pendingRows.length = 0;
    this._pendingIdx = 0;

    const r = this._loadRadius;
    const maxCol = this._config.worldCols;
    const maxRow = this._config.worldRows;

    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const col = centerCol + dx;
        const row = centerRow + dy;
        if (col < 0 || row < 0 || col >= maxCol || row >= maxRow) continue;

        const key = `${col},${row}`;
        if (this._cache.has(key)) {
          this._cache.get(key);
        } else {
          this._pendingCols.push(col);
          this._pendingRows.push(row);
        }
      }
    }
  }

  /** Create a batch of pending clusters and notify world. */
  private _processQueue(): void {
    const end = Math.min(
      this._pendingIdx + CLUSTERS_PER_FRAME,
      this._pendingCols.length,
    );

    for (let i = this._pendingIdx; i < end; i++) {
      const col = this._pendingCols[i];
      const row = this._pendingRows[i];
      this._cache.set(
        `${col},${row}`,
        new Cluster(col, row, this._config),
      );
    }

    this._pendingIdx = end;
    this.activeCount = this._cache.size;
    this._engine.events.emit('world:clusters-changed', null);
  }
}
